<template>
  <b-modal
    v-model="showModal"
    title="Add POB"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form
      @submit.prevent="submitHandler"
    >
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <!-- Basic Information -->
        <b-col cols="6">
          <b-form-group
            label-for="company"
          >
            <template #label>
              Company
              <span
                v-if="isFieldRequired('company')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="company"
              v-model="selectedCompany"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="companyOptions"
              :class="{ 'error-border': validationErrors.company }"
              label="text"
              @input="handleCompanyChange"
            />
            <p
              v-if="validationErrors.company"
              class="error-message"
            >
              {{ validationErrors.company }}
            </p>
            <p
              v-if="errorMessage && errorMessage.company"
              class="error-message"
            >
              {{ errorMessage.company[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label-for="mr"
          >
            <template #label>
              MR
              <span
                v-if="isFieldRequired('mr')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="mr"
              v-model="selectedMr"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="mrOptions"
              :loading="mrLoading"
              :class="{ 'error-border': validationErrors.mr }"
              label="text"
              multiple
              placeholder="Select MR"
              @open="handleMrFocus"
              @input="handleMrChange"
            />
            <p
              v-if="validationErrors.mr"
              class="error-message"
            >
              {{ validationErrors.mr }}
            </p>
            <p
              v-if="mrLoadingError"
              class="error-message"
            >
              {{ mrLoadingError }}
            </p>
            <p
              v-if="errorMessage && errorMessage.mr"
              class="error-message"
            >
              {{ errorMessage.mr[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label-for="party"
          >
            <template #label>
              Party
              <span
                v-if="isFieldRequired('party')"
                class="text-danger"
              >*</span>
            </template>
            <custom-v-select
              id="party"
              v-model="selectedParty"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="partyOptions"
              :class="{ 'error-border': validationErrors.party }"
              label="text"
              @input="handlePartyChange"
            />
            <p
              v-if="validationErrors.party"
              class="error-message"
            >
              {{ validationErrors.party }}
            </p>
            <p
              v-if="errorMessage && errorMessage.party"
              class="error-message"
            >
              {{ errorMessage.party[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            :label="'Mode Of Giving POB' + (isFieldRequired('pob_mode') ? ' *' : '')"
            label-for="pobMode"
          >
            <custom-v-select
              id="pobMode"
              v-model="selectedPobMode"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pobModeOptions"
              :class="{ 'error-border': validationErrors.pob_mode }"
              label="text"
              @input="handlePobModeChange"
            />
            <p
              v-if="validationErrors.pob_mode"
              class="error-message"
            >
              {{ validationErrors.pob_mode }}
            </p>
            <p
              v-if="errorMessage && errorMessage.pob_mode"
              class="error-message"
            >
              {{ errorMessage.pob_mode[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            :label="'Scan Of Paper (PDF or Image file)' + (isFieldRequired('pob_file') ? ' *' : '')"
            label-for="pobFile"
          >
            <b-form-file
              id="pobFile"
              v-model="pobFile"
              :class="{ 'error-border': validationErrors.pob_file }"
              accept=".pdf,.doc,.docx,.jpg,.png,.jpeg"
              placeholder="Choose POB file..."
            />
            <p
              v-if="validationErrors.pob_file"
              class="error-message"
            >
              {{ validationErrors.pob_file }}
            </p>
            <p
              v-if="errorMessage && errorMessage.pob_file"
              class="error-message"
            >
              {{ errorMessage.pob_file[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- POB Items Section -->
        <b-col cols="12">
          <h5 class="mt-1 mb-2">
            POB Items <span class="text-danger">*</span>
          </h5>
          <div
            v-for="(item, index) in pobItems"
            :key="index"
          >
            <b-row>
              <b-col
                cols="6"
                class="d-flex align-items-center justify-content-center"
              >
                <span
                  class="h3 mx-1"
                >{{ index + 1 }}.</span>

                <b-form-group
                  :label-for="`product-${index+1}`"
                  class="w-100"
                >
                  <custom-v-select
                    :id="`product-${index}`"
                    v-model="item.product"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="item.filteredProductOptions || []"
                    :loading="productOptionsLoading"
                    :class="{ 'error-border': validationErrors[`product_${index}`] || item.isDuplicate }"
                    label="display_text"
                    placeholder="Search product..."
                    :filterable="false"
                    @search="(query) => searchProducts(query, index)"
                    @input="(product) => handleProductChange(product, index)"
                    @open="() => onProductDropdownOpen(index)"
                  />
                  <!-- Show validation error for this specific item -->
                  <p
                    v-if="validationErrors[`product_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`product_${index}`] }}
                  </p>
                  <!-- Show duplicate error for this specific item -->
                  <p
                    v-if="item.isDuplicate"
                    class="error-message"
                  >
                    This item is already selected in another row
                  </p>
                </b-form-group>
              </b-col>
              <b-col
                cols="2"
                class="d-flex align-items-center justify-content-center"
              >
                <span
                  v-if="item.rate > 0"
                  class="h5 mx-1"
                >  {{ item.rate }} ₹</span>
                <span
                  v-else
                  class="h5 mx-1"
                >  0</span>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <div class="d-flex gap-2">
                    <b-form-input
                      :id="`quantity-${index}`"
                      v-model="item.quantity"
                      type="number"
                      min="0"
                      :class="{ 'error-border': validationErrors[`quantity_${index}`] }"
                      class="mr-1"
                      placeholder="Qty"
                    />
                    <p
                      class="h2 mr-1"
                      style="margin-top: 5px;"
                    >
                      +
                    </p>
                    <b-form-input
                      :id="`free-quantity-${index}`"
                      v-model="item.free_quantity"
                      type="number"
                      min="0"
                      placeholder="Free"
                    />
                  </div>
                  <p
                    v-if="validationErrors[`quantity_${index}`]"
                    class="error-message"
                  >
                    {{ validationErrors[`quantity_${index}`] }}
                  </p>
                </b-form-group>
              </b-col>

              <b-col
                cols="1"
                class="d-flex align-items-center justify-content-center"
                style="padding-bottom: 10px;"
              >
                <feather-icon
                  v-if="pobItems.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeItem(index)"
                />
              </b-col>
            </b-row>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <b-button
              variant="outline-primary"
              @click="addItem"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
                class="mr-1"
              />
              Add Item
            </b-button>

            <div class="total-order-value">
              <h5 class="mb-0 text-primary">
                Total Order Value: <strong> ₹ {{ totalOrderValue.toFixed(2) }}</strong>
              </h5>
            </div>
          </div>
        </b-col>

        <!-- POB Remarks Section -->
        <b-col cols="12">
          <h5 class="mt-2">
            POB Remarks
          </h5>
          <div
            v-for="(remark, index) in pobRemarks"
            :key="`remark-${index}`"
          >
            <b-row>
              <span
                class="h3 ml-2"
              >{{ index + 1 }}.</span>
              <b-col cols="10">
                <b-form-group
                  :label-for="`remarks-${index+1}`"
                >
                  <b-form-textarea
                    :id="`remarks-${index}`"
                    v-model="remark.remarks"
                    rows="2"
                    placeholder="Enter remarks"
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="1"
                class="d-flex align-items-center justify-content-center"
                style="padding-bottom: 15px;"
              >
                <feather-icon
                  v-if="pobRemarks.length > 1"
                  title="Delete"
                  icon="TrashIcon"
                  size="25"
                  color="red"
                  class="cursor-pointer"
                  @click="removeRemark(index)"
                />
              </b-col>
            </b-row>
          </div>

          <b-button
            variant="outline-primary"
            @click="addRemark"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            Add Remark
          </b-button>
        </b-col>

      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        Create POB
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert, BFormTextarea, BFormFile,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getParsedQueryString from '@/utils/headerQueryParser'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormTextarea,
    BFormFile,
  },

  data() {
    return {
      showModal: true,
      selectedCompany: null,
      companyOptions: [],
      selectedMr: null,
      mrOptions: [],
      mrLoading: false,
      mrLoadingError: null,
      selectedParty: null,
      partyOptions: [],
      selectedPobMode: { value: 'call', text: 'Call' }, // Default
      pobModeOptions: [
        { value: 'call', text: 'Call' },
        { value: 'paper', text: 'Paper' },
        { value: 'whatsapp', text: 'WhatsApp' },
      ],
      placedAtDate: null,
      pobFile: null,
      productOptions: [], // Global product options loaded once
      productOptionsLoading: false,
      pobItems: [
        {
          product: null,
          quantity: null,
          rate: null,
          free_quantity: null,
          filteredProductOptions: [],
          isDuplicate: false,
        },
      ],
      pobRemarks: [
        {
          remarks: '',
        },
      ],
      saving: false,
      errorMessage: {},
      validationErrors: {},
      searchTimeouts: {}, // To handle debouncing for each item
      // Define mandatory fields
      mandatoryFields: ['company', 'mr', 'party', 'pob_mode'],
    }
  },

  computed: {
    totalOrderValue() {
      return this.pobItems.reduce((total, item) => {
        const quantity = parseFloat(item.quantity) || 0
        const rate = parseFloat(item.rate) || 0
        return total + (quantity * rate)
      }, 0)
    },
  },

  created() {
    this.fetchOptions()
    this.fetchProductOptions()
  },

  beforeDestroy() {
    // Clear all timeouts when component is destroyed
    Object.values(this.searchTimeouts).forEach(timeout => {
      clearTimeout(timeout)
    })
  },

  methods: {
    isFieldRequired(fieldName) {
      return this.mandatoryFields.includes(fieldName)
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      // Validate mandatory fields
      if (!this.selectedCompany) {
        this.validationErrors.company = 'Company is required'
        isValid = false
      }

      if (!this.selectedMr || this.selectedMr.length === 0) {
        this.validationErrors.mr = 'MR is required'
        isValid = false
      }

      if (!this.selectedParty) {
        this.validationErrors.party = 'Party is required'
        isValid = false
      }

      if (!this.selectedPobMode) {
        this.validationErrors.pob_mode = 'Mode of giving POB is required'
        isValid = false
      }

      // Validate POB Items (at least one item with product and quantity)
      const validItems = this.pobItems.filter(item => item.product && (parseFloat(item.quantity) > 0 || parseFloat(item.free_quantity) > 0))

      if (validItems.length === 0) {
        this.validationErrors.pob_items = 'At least one POB item with product and quantity is required'
        isValid = false
      }

      // Validate individual items
      this.pobItems.forEach((item, index) => {
        if (item.product && !item.quantity && !item.free_quantity) {
          this.validationErrors[`quantity_${index}`] = 'Quantity required'
          isValid = false
        }

        if ((item.quantity || item.free_quantity) && !item.product) {
          this.validationErrors[`product_${index}`] = 'Product is required when quantity is provided'
          isValid = false
        }
      })

      // Check for duplicates
      this.checkForDuplicates()
      const hasDuplicates = this.pobItems.some(item => item.isDuplicate)
      if (hasDuplicates) {
        isValid = false
      }

      return isValid
    },

    fetchOptions() {
      // Fetch company options
      axios.get('/company_options')
        .then(res => {
          this.companyOptions = res.data.data
        })

      // Fetch party options
      axios.get('/accounts/buyers_options')
        .then(res => {
          this.partyOptions = res.data.data
        })
    },

    async fetchProductOptions() {
      this.productOptionsLoading = true
      try {
        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: `${product.name}`,
          rate: parseFloat(product.rate),
          mrp: parseFloat(product.mrp),
        }))

        // Initialize first item with first 15 products for better performance
        this.$set(this.pobItems[0], 'filteredProductOptions', this.productOptions.slice(0, 15))

        this.productOptionsLoading = false
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Failed to load products'
        this.productOptionsLoading = false
      }
    },

    // Handle product dropdown open - show initial products immediately
    onProductDropdownOpen(itemIndex) {
      const item = this.pobItems[itemIndex]
      if (!item.filteredProductOptions || item.filteredProductOptions.length === 0) {
        this.$set(this.pobItems[itemIndex], 'filteredProductOptions', this.productOptions.slice(0, 15))
      }
    },

    handleCompanyChange(company) {
      // Clear validation error immediately when company is selected
      if (this.validationErrors.company) {
        this.$delete(this.validationErrors, 'company')
      }

      // Reset MR selection when company changes
      this.selectedMr = null
      this.mrOptions = []
      this.mrLoadingError = null

      // Clear all search timeouts
      Object.values(this.searchTimeouts).forEach(timeout => {
        clearTimeout(timeout)
      })
      this.searchTimeouts = {}

      if (company && company.id) {
        this.fetchMrOptions(company.id)
      }
    },

    handleMrChange() {
      // Clear validation error immediately when MR is selected
      if (this.validationErrors.mr) {
        this.$delete(this.validationErrors, 'mr')
      }
    },

    handlePartyChange() {
      // Clear validation error immediately when party is selected
      if (this.validationErrors.party) {
        this.$delete(this.validationErrors, 'party')
      }
    },

    handlePobModeChange() {
      // Clear validation error immediately when pob mode is selected
      if (this.validationErrors.pob_mode) {
        this.$delete(this.validationErrors, 'pob_mode')
      }
    },

    async fetchMrOptions(companyId) {
      const params = { company: companyId, is_active: 'True' }

      this.mrLoading = true
      this.mrLoadingError = null

      try {
        const res = await axios.get(`/mr/?${getParsedQueryString(params)}`)
        this.mrOptions = res.data.results.map(item => {
          // Find primary mobile number
          const primaryMobile = item.mr_mobiles?.find(mobile => mobile.is_primary)?.mobile || ''

          // Create display text with name and primary mobile
          const displayText = primaryMobile
            ? `${item.name} ( ${primaryMobile} )`
            : item.name

          return {
            id: item.id,
            text: displayText,
            name: item.name,
            mobile: primaryMobile,
          }
        })
        if (this.mrOptions.length === 1 && !this.selectedMr) {
          this.selectedMr = [this.mrOptions[0]]
        }
        this.mrLoading = false
      } catch (error) {
        this.mrLoadingError = error?.response?.data?.error || 'Something went wrong'
        this.mrLoading = false
      }
    },

    // Client-side product search function with optimized performance
    searchProducts(query, itemIndex) {
      // Clear existing timeout for this item
      if (this.searchTimeouts[itemIndex]) {
        clearTimeout(this.searchTimeouts[itemIndex])
      }

      // Set new timeout for debouncing
      this.searchTimeouts[itemIndex] = setTimeout(() => {
        if (!query || query.trim() === '') {
          // Show first 15 products when query is empty
          this.$set(this.pobItems[itemIndex], 'filteredProductOptions', this.productOptions.slice(0, 15))
        } else {
          // Filter products by name (case-insensitive)
          const searchTerm = query.toLowerCase()
          const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchTerm))
          // Limit to 50 results for performance
          this.$set(this.pobItems[itemIndex], 'filteredProductOptions', filtered.slice(0, 50))
        }
      }, 200) // Reduced to 200ms for faster response
    },

    handleProductChange(product, itemIndex) {
      // Clear validation error for this item using $delete
      if (this.validationErrors[`product_${itemIndex}`]) {
        this.$delete(this.validationErrors, `product_${itemIndex}`)
      }

      // Check if this product is already selected in another item
      const isDuplicate = this.pobItems.some((item, index) => {
        if (index === itemIndex) return false // Don't compare with itself
        return item.product && product
               && ((item.product.id && item.product.id === product.id)
                || (item.product.code && item.product.code === product.code))
      })

      if (isDuplicate) {
        // Show toast error for duplicate selection
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Duplicate Product',
            text: 'This product is already selected in another item',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })

        // Don't set the product if it's a duplicate
        this.$set(this.pobItems[itemIndex], 'product', null)
        this.$set(this.pobItems[itemIndex], 'rate', null)
        this.$set(this.pobItems[itemIndex], 'mrp', null)
        return
      }
      // Set the product if it's not a duplicate
      this.$set(this.pobItems[itemIndex], 'product', product)
      this.$set(this.pobItems[itemIndex], 'rate', product?.rate)
      this.$set(this.pobItems[itemIndex], 'mrp', product?.mrp)
      // Check for duplicates and update isDuplicate flag for all items
      this.checkForDuplicates()
    },

    handleMrFocus() {
      if (!this.selectedCompany) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Company Required',
            text: 'Please select a company first before selecting MR',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },

    checkForDuplicates() {
      // Reset all duplicate flags
      this.pobItems.forEach((item, index) => {
        this.$set(this.pobItems[index], 'isDuplicate', false)
      })

      // Check for duplicates
      const selectedProducts = {}
      this.pobItems.forEach((item, index) => {
        if (item.product && (item.product.id || item.product.code)) {
          const productId = item.product.id || item.product.code

          if (selectedProducts[productId]) {
            // Mark both current and previous as duplicates
            this.$set(this.pobItems[index], 'isDuplicate', true)
            this.$set(this.pobItems[selectedProducts[productId]], 'isDuplicate', true)
          } else {
            selectedProducts[productId] = index
          }
        }
      })
    },

    addItem() {
      // Check if any existing item doesn't have a product selected
      const hasEmptyItems = this.pobItems.some(item => !item.product)

      if (hasEmptyItems) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Incomplete Items',
            text: 'Please select a product for all existing items before adding a new one',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.pobItems.push({
        product: null,
        quantity: null,
        free_quantity: null,
        rate: null,
        mrp: null,
        filteredProductOptions: this.productOptions.slice(0, 15),
        isDuplicate: false,
      })
    },

    removeItem(index) {
      // Clear timeout if exists
      if (this.searchTimeouts[index]) {
        clearTimeout(this.searchTimeouts[index])
        delete this.searchTimeouts[index]
      }

      // Clear validation errors for this item
      if (this.validationErrors[`product_${index}`]) {
        this.$delete(this.validationErrors, `product_${index}`)
      }
      if (this.validationErrors[`quantity_${index}`]) {
        this.$delete(this.validationErrors, `quantity_${index}`)
      }

      this.pobItems.splice(index, 1)

      // Recheck for duplicates after removal
      this.checkForDuplicates()
    },

    addRemark() {
      this.pobRemarks.push({
        remarks: '',
      })
    },

    removeRemark(index) {
      this.pobRemarks.splice(index, 1)
    },

    submitHandler(event) {
      event.preventDefault()

      // Validate form before submitting
      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.saving = true

      // Create FormData with only two keys
      const formData = new FormData()

      // Prepare all data as JSON object
      const dataPayload = {}

      // Basic information
      if (this.selectedCompany) dataPayload.company = this.selectedCompany.id
      if (this.selectedMr) dataPayload.mr = this.selectedMr.map(item => item.id)
      if (this.selectedParty) dataPayload.party = this.selectedParty.id
      if (this.selectedPlacedBy) dataPayload.placed_by = this.selectedPlacedBy.id
      if (this.selectedPobMode) dataPayload.pob_mode = this.selectedPobMode.value
      if (this.selectedStatus) dataPayload.status = this.selectedStatus.value
      if (this.placedAtDate) dataPayload.placed_at = this.placedAtDate

      // Prepare POB Items
      const validItems = this.pobItems
        .filter(item => item.product && (item.quantity > 0 || item.free_quantity > 0))
        .map(item => ({
          product: String(item.product.name || item.product.display_text),
          code: String(item.product.code || item.product.id),
          quantity: parseInt(item.quantity, 10) || 0,
          free_quantity: parseInt(item.free_quantity, 10) || 0,
          rate: item.rate || 0,
          mrp: item.mrp || 0,
        }))
      if (validItems.length > 0) {
        dataPayload.pob_items = validItems
      }

      // Prepare POB Remarks
      const validRemarks = this.pobRemarks
        .filter(remark => remark.remarks && remark.remarks.trim().length > 0)
        .map(remark => ({
          remarks: String(remark.remarks.trim()),
        }))

      if (validRemarks.length > 0) {
        dataPayload.pob_remarks = validRemarks
      }

      // Add data as JSON string
      formData.append('data', JSON.stringify(dataPayload))

      // Add file (even if null/undefined, FormData will handle it)
      if (this.pobFile) {
        formData.append('pob_file', this.pobFile)
      }

      // Send request
      axios.post('/marg/create-pob/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
        },
      })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'POB Created Successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('pob-added')
          this.saving = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
  /* Remove the box-shadow to avoid extra border effect */
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.total-order-value {
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.text-danger {
  color: #dc3545 !important;
}
</style>
